import axios, { AxiosResponse } from 'axios';
import mime from 'mime';
import { generateHeaders } from 'utils/APIUtils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError } from '../Reject';
import { RootState } from '../index';
import FileObj from 'domain/File';
import { logout } from './auth';
import { handleApiError } from '../handleApiError';

export const FileUploadStatuses = {
    REQUESTED_UPLOAD: 'requested-upload',
    REQUESTED_UPLOAD_FAIL: 'requested-upload-fail',
    UPLOADING: 'uploading',
    UPLOADING_FAIL: 'uploading-fail',
    UPLOAD_FINISHED: 'upload-finished',
    UPLOAD_FINISHED_FAIL: 'upload-finished-fail',
    UPLOADED: 'uploaded',
    DELETING: 'deleting',
    DELETE_FAILED: 'delete-failed',
    DELETED: 'deleted',
} as const;

type ValueOf<T> = T[keyof T];

export type FileUploadStatus = ValueOf<typeof FileUploadStatuses>;

export interface FileUpload extends FileObj { // TODO:review
    file: File;
    fileName: string;
    fileType: string;
    status: FileUploadStatus;
    url: string;
}

interface FileState {
    files: FileUpload[];
}

const initialState: FileState = {
    files: [],
};

interface NotifyFileUploadResponse {
    url: string;
    idFile: string;
}

interface NotifyFileUploadedParams {
    idFile: string;
    category: string;
    fileName: string;
    fileType: string;
}


export const notifyFileUploaded = createAsyncThunk<
    NotifyFileUploadResponse,
    NotifyFileUploadedParams,
    {
        state: RootState;
        rejectValue: ApiError
    }
>(
    'notifyFileUpload',
    async (params, thunkAPI) => {
        const { idFile, category } = params;
        const state = thunkAPI.getState();
        const { auth } = state;
        const headers = generateHeaders(auth.jwt);

        try {
            const response: AxiosResponse<NotifyFileUploadResponse> = await axios.put(
                `${process.env.REACT_APP_API_URL}/file/${idFile}`,
                { category },
                { headers }
            );
            return response.data;

        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

type RemoveFileParams = FileUpload

export const removeFile = createAsyncThunk<{ success: boolean }, RemoveFileParams, {
    state: RootState;
    rejectValue: ApiError
}>(
    'deleteFile',
    async (params, thunkAPI) => {
        const { idFile } = params;
        const { auth } = thunkAPI.getState();
        const headers = generateHeaders(auth.jwt);

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/file/${idFile}`, { headers });
            return { success: true };
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);


type UploadFileResponse = FileObj

interface UploadParams {
    file: File;
    category: string;
    idUser: string;
    uniqueSeed: number;
}

interface FilenameParts {
    fileName: string;
    fileType: string;
}

export const getFilenameParts = (
    file: File,
    idUser: string,
    uniqueSeed: number
): FilenameParts => {
    const fullFileName = file.name;
    const fileNameParts = fullFileName.split('.');

    const extension = fileNameParts.length > 1
        ? fileNameParts[fileNameParts.length - 1]
        : '';

    const fileName = extension
        ? `${idUser}-${uniqueSeed}.${extension}`
        : `${idUser}-${uniqueSeed}`;

    const fileBlob = `${mime.getType(fullFileName)}`;

    const fileType = fileBlob || 'application/octet-stream'; // Fallback to a default MIME type

    return {
        fileName,
        fileType,
    };
};

export const upload = createAsyncThunk<UploadFileResponse, UploadParams, {
    state: RootState;
    rejectValue: ApiError
}>(
    'uploadFile',
    async (params, thunkAPI) => {
        const { file, category, idUser, uniqueSeed } = params;
        const { auth } = thunkAPI.getState();
        const headers = generateHeaders(auth.jwt);

        try {
            const { fileName, fileType } = getFilenameParts(file, idUser, uniqueSeed);

            const response = await axios.post<FileObj>(
                `${process.env.REACT_APP_API_URL}/file`,
                { fileName, fileType },
                { headers }
            );

            const { signedRequest, idFile } = response.data;

            await axios.put(signedRequest, file, {
                headers: {
                    'Content-Type': fileType,
                },
            });


            await thunkAPI.dispatch(
                notifyFileUploaded({
                    idFile,
                    category,
                    fileName,
                    fileType,
                })
            );

            return response.data;

        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

const fileSlice = createSlice({
    name: 'file',
    initialState,
    reducers: {
        cleanUnfinishedUploads: (state) => ({
            ...state,
            files: state.files.filter((f) => f.status === FileUploadStatuses.UPLOADED),
        })
    },
    extraReducers: (builder) => {
        builder
            .addCase(upload.pending, (state, action) => {
                const { file, idUser, uniqueSeed } = action?.meta?.arg || {};
                const { fileName, fileType } = getFilenameParts(file, idUser, uniqueSeed);
                state.files.push({
                    fileName,
                    fileType,
                    status: FileUploadStatuses.REQUESTED_UPLOAD,
                } as FileUpload);
            })
            .addCase(upload.rejected, (state, action) => {
                const { file, idUser, uniqueSeed } = action?.meta?.arg || {};
                const { fileName, fileType } = getFilenameParts(file, idUser, uniqueSeed);
                const fileIndex = state.files.findIndex((file) => file.fileName === fileName && file.fileType === fileType);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        status: FileUploadStatuses.REQUESTED_UPLOAD,
                    };
                }
            })
            .addCase(upload.fulfilled, (state, action) => {
                const { file, idUser, uniqueSeed } = action?.meta?.arg || {};
                const { fileName, fileType } = getFilenameParts(file, idUser, uniqueSeed);
                const fileIndex = state.files.findIndex((file) => file.fileName === fileName && file.fileType === fileType);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        status: FileUploadStatuses.UPLOAD_FINISHED, // I more or less know that if this succeeds, the upload to S3 would have started.
                    };
                }
            })
            .addCase(notifyFileUploaded.rejected, (state, action) => {
                const { fileName, fileType } = action?.meta?.arg || {};
                const fileIndex = state.files.findIndex((file) => file.fileName === fileName && file.fileType === fileType);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        status: FileUploadStatuses.UPLOADING_FAIL,
                    };
                }
            })
            .addCase(notifyFileUploaded.fulfilled, (state, action) => {
                const { fileName, fileType } = action?.meta?.arg || {};
                const fileIndex = state.files.findIndex((file) => file.fileName === fileName && file.fileType === fileType);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        url: action.payload ? action.payload.url : null,
                        idFile: action.payload.idFile,
                        status: FileUploadStatuses.UPLOADED,
                    };
                }
            })
            .addCase(removeFile.pending, (state, action) => {
                const { idFile } = action?.meta?.arg || {};
                const fileIndex = state.files.findIndex((file) => file.idFile === idFile);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        status: FileUploadStatuses.DELETING,
                    };
                }
            })
            .addCase(removeFile.rejected, (state, action) => {
                const { idFile } = action?.meta?.arg || {};
                const fileIndex = state.files.findIndex((file) => file.idFile === idFile);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        status: FileUploadStatuses.DELETE_FAILED,
                    };
                }
            })
            .addCase(removeFile.fulfilled, (state, action) => {
                const { idFile } = action?.meta?.arg || {};
                const fileIndex = state.files.findIndex((file) => file.idFile === idFile);
                if (fileIndex !== -1) {
                    state.files[fileIndex] = {
                        ...state.files[fileIndex],
                        status: FileUploadStatuses.DELETED,
                    };
                }
            })
            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export const { cleanUnfinishedUploads } = fileSlice.actions;
export default fileSlice.reducer;
